import type { JSX, ReactNode } from 'react';
import { Select, SelectItem, SelectSection } from '@heroui/react';
import type { SelectedItems } from '@heroui/react';

import { useTenant } from '~/hooks/useTenant';
import { logger } from '~/utils/logger';

import { RoleBadge } from './RoleBadge';

/**
 * Organization switcher component for users with multiple organizations.
 *
 * Features:
 * - Only visible when user has >1 organization
 * - Persists selection to localStorage
 * - Triggers cache invalidation on org switch
 * - Shows role badge for each organization
 * - Compact dropdown UI
 *
 * Story: COM-134 - Multi-org support
 */
export function OrgSwitcher(): JSX.Element | null {
  const { currentOrg, organizations, setActiveOrg } = useTenant();

  // Hide if user has 0 or 1 organizations
  if (!organizations || organizations.length <= 1) {
    return null;
  }

  const handleOrgChange = (orgId: string): void => {
    logger.info({ component: 'OrgSwitcher' }, 'User switching organization', {
      from: currentOrg?.id,
      to: orgId,
    });
    setActiveOrg(orgId);
  };

  // Two-line trigger: a small "Organization" header above the current org's
  // name. `items` can be empty even with a valid selection mid-render, so
  // never assume items[0] exists.
  const renderValue = (items: SelectedItems): ReactNode => (
    <span className="flex min-w-0 flex-col items-start justify-center leading-tight">
      <span className="text-tiny text-nav-active-text">Organization</span>
      <span className="truncate text-small font-medium">{items[0]?.textValue ?? ''}</span>
    </span>
  );

  return (
    <div className="w-full" data-testid="org-switcher">
      <Select
        selectedKeys={currentOrg?.id ? [currentOrg.id] : []}
        onSelectionChange={keys => {
          const selectedId = Array.from(keys)[0] as string;
          if (selectedId && selectedId !== currentOrg?.id) {
            handleOrgChange(selectedId);
          }
        }}
        renderValue={renderValue}
        classNames={{
          mainWrapper: 'w-full',
          // size="sm" fixes the trigger at h-8, which clips the two-line
          // renderValue content above — let it grow to fit instead.
          trigger: 'h-auto py-1.5',
          // The popover's width isn't derived from the trigger's rendered
          // width, so it needs its own min-width to keep long org names +
          // role badges from being clipped inside the open list.
          popoverContent: 'min-w-[280px]',
        }}
        size="sm"
        aria-label="Select organization"
      >
        {/* Section title is a non-selectable heading, never part of the option
            collection — clicking it cannot trigger onSelectionChange. */}
        <SelectSection
          key="organizations"
          title="Organization"
          classNames={{ heading: 'text-nav-active-text' }}
        >
          {organizations.map(org => (
            <SelectItem
              key={org.id}
              textValue={org.name}
              endContent={<RoleBadge role={org.role} />}
              className={org.id === currentOrg?.id ? 'bg-primary/10 text-foreground' : ''}
            >
              <span className="font-medium">{org.name}</span>
            </SelectItem>
          ))}
        </SelectSection>
      </Select>
    </div>
  );
}
